import { Table, TableBody, TableHead, TableRow } from "@mui/material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { StyledTableCell } from "../styles";
import { GroupMathRow, GroupMathRowHeader } from "../types";

type Props = {
  rowData: any; // Combined data from both states
  assignmentName?: string;
  tableData?: any; // Full table data to find viikkokeskityömäärälaskenta
};

const AccumulationsTableGroupMathTable = ({
  rowData,
  assignmentName,
  tableData,
}: Props) => {
  const { t } = useTranslation("talentProfile");

  // Headers: same as MultiCalculationTable + Työviikot
  const headers: GroupMathRowHeader[] = [
    { key: "valueDate", label: t("accumulationKeys.valueDate") },
    { key: "salary", label: t("accumulationKeys.salary") },
    { key: "workTime", label: t("accumulationKeys.workTimeMulti") },
    { key: "inputOperand", label: "Työviikot" },
  ];

  // Extract data from lomakeskiansiolaskenta (ongoingAverages)
  const buildGroupMathRows = (): GroupMathRow[] => {
    const rows: GroupMathRow[] = [];
    const lomakeskiansiolaskentaRaw = rowData.lomakeskiansiolaskenta;
    const tyotuntienKeskimaaraRaw = rowData.viikkokeskityömäärälaskenta;

    // Parse lomakeskiansiolaskenta if it's a string
    let lomakeskiansiolaskenta: any;
    if (typeof lomakeskiansiolaskentaRaw === "string") {
      try {
        lomakeskiansiolaskenta = JSON.parse(lomakeskiansiolaskentaRaw);
      } catch (e) {
        // Failed to parse, keep as undefined
      }
    } else {
      lomakeskiansiolaskenta = lomakeskiansiolaskentaRaw;
    }

    // Parse partitions
    let parsedPartitions: any;
    if (typeof tyotuntienKeskimaaraRaw === "string") {
      try {
        parsedPartitions = JSON.parse(tyotuntienKeskimaaraRaw);
      } catch (e) {
        // Failed to parse, keep as undefined
      }
    } else {
      parsedPartitions = tyotuntienKeskimaaraRaw;
    }

    if (lomakeskiansiolaskenta?.ongoingAverages) {
      const ongoingAvgKeys = Object.keys(
        lomakeskiansiolaskenta.ongoingAverages,
      );

      ongoingAvgKeys.forEach((dateKey, index) => {
        const avgData = lomakeskiansiolaskenta.ongoingAverages[dateKey];

        // Find matching partition by dateKey (which matches the first part of partition key)
        let inputOperand = "";
        if (parsedPartitions?.partitions) {
          const partitionKeys = Object.keys(parsedPartitions.partitions);

          let matchingPartitionKey;

          if (dateKey === "NO_DATE") {
            // Special case: when dateKey is "NO_DATE", try to match by valueDate
            // or use the first (and likely only) partition if there's only one
            if (avgData?.valueDate) {
              // Extract just the date part (YYYY-MM-DD) from valueDate
              const valueDateOnly = avgData.valueDate.split("T")[0];
              matchingPartitionKey = partitionKeys.find((pKey) =>
                pKey.startsWith(valueDateOnly),
              );
            }

            // Fallback: if only one partition exists, use it
            if (!matchingPartitionKey && partitionKeys.length === 1) {
              matchingPartitionKey = partitionKeys[0];
            }
          } else {
            // Normal case: match by dateKey prefix
            matchingPartitionKey = partitionKeys.find((pKey) =>
              pKey.startsWith(dateKey),
            );
          }

          if (matchingPartitionKey) {
            inputOperand =
              parsedPartitions.partitions[matchingPartitionKey]?.inputOperand ||
              "";
          }
        }

        rows.push({
          assignmentId: assignmentName || "",
          salary: avgData?.salary || "",
          workTime: avgData?.workTime || "",
          inputOperand,
          valueDate: avgData?.valueDate || "",
        });
      });
    }

    return rows;
  };

  const renderValue = (key: string, value?: string | number) => {
    if (key === "valueDate" && value) {
      return format(new Date(value), "dd.MM.yyyy");
    }
    return value !== undefined && value !== null ? value : "0";
  };

  const rows = buildGroupMathRows();

  return (
    <StyledTableCell>
      <Table>
        <TableHead>
          <TableRow>
            {headers.map((header) => (
              <StyledTableCell key={header.key} align="right">
                {header.label}
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={`TableRow_${index}`}>
              {headers.map((header) => (
                <StyledTableCell key={header.key} align="right">
                  {renderValue(header.key, row[header.key])}
                </StyledTableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </StyledTableCell>
  );
};

export default AccumulationsTableGroupMathTable;
